import { useTranslation } from "react-i18next";
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Empty, Pagination, Spin } from 'antd';
import { PaginationProps } from 'antd/lib';
import React, { FunctionComponent, useMemo, useState, useEffect, useCallback } from 'react';
import './card-data.scss';

export type CardDataProps = {
	data?: any[];
	renderItem: (item: any, index: number) => React.ReactNode;
	loading?: boolean;
	totalRecords?: number;
	pageNumber: number;
	pageSize: number;
	setPageNumber: (page: number) => void;
	setPageSize: (size: number) => void;
	minHeight?: string;
	scroll?: { y: number };
	showFilter?: boolean;
	modalMode?: "select" | "saveTemplate" | "moveItem";
};

const CardData: FunctionComponent<CardDataProps> = ({
	data = [],
	renderItem,
	loading,
	totalRecords = 0,
	pageNumber,
	pageSize,
	setPageNumber,
	setPageSize,
	minHeight,
	scroll,
	showFilter,
	modalMode
}) => {
  const { t } = useTranslation();
	const calculateCardScroll = useCallback(() => {
		const windowHeight = window.innerHeight;
		const windowWidth = window.innerWidth;
		const baseOffset = 150;
		const widthRatio = windowWidth / 1920;
		const dynamicOffset = Math.min(150, Math.max(50, widthRatio * 150));
		// const modalOffset = modalMode ? 100 : 0;
		const filterOffset = showFilter ? 120 : 0;

		const finalOffset = baseOffset + dynamicOffset + filterOffset;
		const yScroll = Math.max(200, windowHeight - finalOffset - 48);

		return { y: Math.round(yScroll) };
	}, [showFilter, modalMode]);

	const [cardScroll, setCardScroll] = useState(calculateCardScroll);

	useEffect(() => {
		let timeoutId: NodeJS.Timeout;

		const handleResize = () => {
			clearTimeout(timeoutId);
			timeoutId = setTimeout(() => {
				setCardScroll(calculateCardScroll());
			}, 16);
		};
		setCardScroll(calculateCardScroll());

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
			clearTimeout(timeoutId);
		};
	}, [calculateCardScroll]);

	useEffect(() => {
		setCardScroll(calculateCardScroll());
	}, [showFilter, modalMode, calculateCardScroll]);

	const onChange: PaginationProps['onChange'] = (page, size) => {
		setPageNumber(page);
		setPageSize(size ?? pageSize);
	};

	const dataWithOrder = useMemo(
		() =>
			data.map((item, idx) => ({
				...item,
				numericalOrder: pageSize * (pageNumber - 1) + idx + 1,
			})),
		[data, pageNumber, pageSize]
	);

	const finalScroll = scroll || cardScroll;

	return (
		<div className="card-data-wrapper">
			<div
				className="card-data-scroll-container"
				style={{
					maxHeight: finalScroll.y,
					overflowY: 'auto',
					overflowX: 'hidden'
				}}
			>
				<Spin spinning={!!loading}>
					{dataWithOrder.length ? (
						<div className="resource-grid">
							{dataWithOrder.map(renderItem)}
						</div>
					) : (
						<div className="folder-empty">
							<Empty
								image={<img
									className="image-empty-data"
									src="/images/empty-img-gray.png"
									alt="empty-img"
								/>}
								description={t("Không tìm thấy", "Không tìm thấy")} />
						</div>
					)}
				</Spin>
			</div>

			{dataWithOrder.length > 0 && (
				<div className="card-data-pagination">
					<Pagination
						total={totalRecords}
						current={pageNumber}
						pageSize={pageSize}
						showSizeChanger
						showQuickJumper
						onChange={onChange}
						itemRender={(_, type, original) =>
							type === 'prev' ? <LeftOutlined /> :
								type === 'next' ? <RightOutlined /> : original
						}
						locale={{
							items_per_page: '/ trang',
							jump_to: t('Đi đến trang', 'Đi đến trang'),
							page: '',
						}}
						className="pagination-table"
						showTotal={(total) => `${t('Tổng số', 'Tổng số')} ${total} ${t('bản ghi', 'bản ghi')}`}
						style={{ paddingRight: 24 }}
					/>
				</div>
			)}
		</div>
	);
};

export default CardData;